import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash, Copy } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

const thisYear = new Date().getFullYear();

// Festivos agrupados por año: solo se ve y se edita el año seleccionado.
export function HolidaysByYear({ holidays = [], onChange, lang }) {
  const es = lang === 'es';
  const [extraYears, setExtraYears] = useState([]);
  const [year, setYear] = useState(String(thisYear));
  const [draft, setDraft] = useState({ date: '', name: '' });

  const counts = useMemo(() => holidays.reduce((acc, h) => {
    const y = (h.date || '').slice(0, 4);
    acc[y] = (acc[y] || 0) + 1;
    return acc;
  }, {}), [holidays]);
  const years = useMemo(
    () => [...new Set([String(thisYear), ...Object.keys(counts), ...extraYears, year])].filter(Boolean).sort(),
    [counts, extraYears, year],
  );
  const shown = holidays.filter(h => (h.date || '').startsWith(`${year}-`));

  const addYear = () => {
    const next = String(Math.max(...years.map(Number)) + 1);
    setExtraYears(p => [...p, next]);
    setYear(next);
  };

  const prevYear = String(Number(year) - 1);
  const copyPrev = () => {
    const taken = new Set(holidays.map(h => h.date));
    const copies = holidays
      .filter(h => h.date.startsWith(`${prevYear}-`) && h.date.slice(5) !== '02-29')
      .map(h => ({ ...h, date: `${year}-${h.date.slice(5)}` }))
      .filter(h => !taken.has(h.date));
    if (!copies.length) { toast.info(es ? 'No hay festivos nuevos que copiar' : 'Nothing new to copy'); return; }
    onChange([...holidays, ...copies].sort((a, b) => a.date.localeCompare(b.date)));
    toast.success(es ? `${copies.length} festivos copiados de ${prevYear}. Revisa las fechas móviles (Semana Santa…) y pulsa Guardar.` : `${copies.length} holidays copied from ${prevYear}.`);
  };

  const editRow = (oldDate, patch) => {
    if (patch.date !== undefined) {
      if (!patch.date || !patch.date.startsWith(`${year}-`)) { toast.error(es ? `La fecha debe ser del año ${year}` : `Date must be in ${year}`); return false; }
      if (patch.date !== oldDate && holidays.some(h => h.date === patch.date)) { toast.error(es ? 'Fecha ya en la lista' : 'Date already in list'); return false; }
    }
    onChange(holidays.map(h => (h.date === oldDate ? { ...h, ...patch } : h)).sort((a, b) => a.date.localeCompare(b.date)));
  };

  const add = () => {
    if (!draft.date) { toast.error(es ? 'Introduce una fecha' : 'Enter a date'); return; }
    if (!draft.date.startsWith(`${year}-`)) { toast.error(es ? `La fecha debe ser del año ${year}` : `Date must be in ${year}`); return; }
    if (holidays.some(h => h.date === draft.date)) { toast.error(es ? 'Fecha ya en la lista' : 'Date already in list'); return; }
    onChange([...holidays, { ...draft }].sort((a, b) => a.date.localeCompare(b.date)));
    setDraft({ date: '', name: '' });
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-stone-500">{es ? 'Festivos' : 'Holidays'}</h4>
        <Select value={year} onValueChange={v => { setYear(v); setDraft({ date: '', name: '' }); }}>
          <SelectTrigger className="w-40 h-8 text-sm" data-testid="wh-holiday-year-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {years.map(y => (
              <SelectItem key={y} value={y} data-testid={`wh-holiday-year-${y}`}>{y} ({counts[y] || 0})</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button size="sm" variant="ghost" className="h-8 gap-1 text-xs" onClick={addYear} data-testid="wh-add-year-btn">
          <Plus size={12} />{es ? 'Añadir año' : 'Add year'}
        </Button>
        {(counts[prevYear] || 0) > 0 && (
          <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" onClick={copyPrev} data-testid="wh-copy-prev-year-btn">
            <Copy size={12} />{es ? `Copiar festivos de ${prevYear}` : `Copy from ${prevYear}`}
          </Button>
        )}
      </div>
      <div className="flex items-end gap-2">
        <div>
          <Label className="text-xs mb-1">{es ? 'Fecha' : 'Date'}</Label>
          <Input type="date" min={`${year}-01-01`} max={`${year}-12-31`} value={draft.date}
            onChange={e => setDraft(p => ({ ...p, date: e.target.value }))} className="w-40 text-sm" data-testid="wh-new-holiday-date" />
        </div>
        <div className="flex-1 max-w-md">
          <Label className="text-xs mb-1">{es ? 'Nombre (opcional)' : 'Name (optional)'}</Label>
          <Input value={draft.name} onChange={e => setDraft(p => ({ ...p, name: e.target.value }))}
            placeholder={es ? 'p. ej. Año Nuevo' : 'e.g. New Year'} className="text-sm" data-testid="wh-new-holiday-name" />
        </div>
        <Button onClick={add} data-testid="wh-add-holiday-btn" variant="outline" className="gap-1">
          <Plus size={14} />{es ? 'Añadir' : 'Add'}
        </Button>
      </div>
      {shown.length === 0 ? (
        <p className="text-xs text-stone-400 py-2" data-testid="wh-holidays-empty">{es ? `Sin festivos definidos para ${year}.` : `No holidays defined for ${year}.`}</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-40">{es ? 'Fecha' : 'Date'}</TableHead>
              <TableHead>{es ? 'Nombre' : 'Name'}</TableHead>
              <TableHead className="w-20"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.map(h => (
              <TableRow key={h.date} data-testid={`wh-holiday-row-${h.date}`}>
                <TableCell>
                  <Input type="date" min={`${year}-01-01`} max={`${year}-12-31`} defaultValue={h.date} key={`d-${h.date}`}
                    onBlur={e => { if (e.target.value !== h.date && editRow(h.date, { date: e.target.value }) === false) e.target.value = h.date; }}
                    className="h-8 w-40 font-mono text-sm" data-testid={`wh-holiday-date-${h.date}`} />
                </TableCell>
                <TableCell>
                  <Input defaultValue={h.name || ''} key={`n-${h.date}`} placeholder="—"
                    onBlur={e => e.target.value !== (h.name || '') && editRow(h.date, { name: e.target.value })}
                    className="h-8 text-sm" data-testid={`wh-holiday-name-${h.date}`} />
                </TableCell>
                <TableCell>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-stone-400 hover:text-red-500"
                    onClick={() => onChange(holidays.filter(x => x.date !== h.date))} data-testid={`wh-remove-holiday-${h.date}`}>
                    <Trash size={14} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <p className="text-[11px] text-stone-400 mt-1">
        {es ? 'Recuerda pulsar Guardar tras añadir o quitar festivos.' : 'Remember to click Save after adding or removing holidays.'}
      </p>
    </div>
  );
}
